import { useEffect, useState } from 'react'
import { Button, Field, HStack, Input, Stack, Text, Textarea, VStack } from '@chakra-ui/react'
import { FiCopy } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { Card } from '../shared'
import { useArrivalInstruction, useSaveArrivalInstruction, type ArrivalKind } from '../../hooks/useBusiness'
import { useLimits } from '../../hooks/useLimits'
import { extractApiMessage } from '../../lib/apiError'
import type { ArrivalInstructionView } from '../../types/business'
import { CopyArrivalModal } from './CopyArrivalModal'

function sourceLine(view: ArrivalInstructionView | undefined, chainLabel?: string): string {
  const effective = view?.effective
  if (!effective) return 'Nothing is shown yet'
  switch (effective.source) {
    case 'Worksite':
      return 'Canvassers see your instruction for this worksite'
    case 'Business':
      return chainLabel ? `Canvassers see your instruction for ${chainLabel}` : 'Canvassers see your chain instruction'
    case 'WorksiteRecord':
      return 'Canvassers see the worksite record'
  }
}

/** Pre-flight counter for a served text cap. Shows nothing while the cap is unknown (loading or
 *  errored); over the cap it warns, but the server's 400 is the only gate. */
function CapCounter({ length, cap }: { length: number; cap: number | undefined }) {
  if (cap === undefined) return null
  const over = length > cap
  return (
    <Field.HelperText color={over ? 'danger.fg' : undefined}>
      {over ? `${length}/${cap} — over the limit` : `${length}/${cap}`}
    </Field.HelperText>
  )
}

function blankToNull(v: string): string | null {
  const t = v.trim()
  return t ? t : null
}

/** O3: organizer card for the committee's own arrival instruction on a worksite or a business
 *  (chain). The source line shows what canvassers see now; "Copy to…" reuses the saved row. */
export function ArrivalInstructionCard({ kind, id, chainLabel }: { kind: ArrivalKind; id: string; chainLabel?: string }) {
  const { data: view } = useArrivalInstruction(kind, id)
  const save = useSaveArrivalInstruction(kind, id)
  const saveMutate = save.mutate
  const [text, setText] = useState('')
  const [textEs, setTextEs] = useState('')
  const [askFor, setAskFor] = useState('')
  const [copyOpen, setCopyOpen] = useState(false)
  const textCaps = useLimits().data?.textCaps
  const textCap = textCaps?.maxArrivalTextLength
  const askForCap = textCaps?.maxArrivalAskForLength

  const own = view?.own ?? null
  // Re-seed the inputs whenever the server's own row changes (load, save, delete). React Query's
  // structural sharing keeps `own` referentially stable across identical refetches.
  useEffect(() => {
    setText(own?.text ?? '')
    setTextEs(own?.textEs ?? '')
    setAskFor(own?.askFor ?? '')
  }, [own])

  // Copy sends the SAVED row, so unsaved edits block it until they are saved.
  const dirty = text !== (own?.text ?? '') || textEs !== (own?.textEs ?? '') || askFor !== (own?.askFor ?? '')

  const handleSave = () => {
    saveMutate(
      { text: blankToNull(text), textEs: blankToNull(textEs), askFor: blankToNull(askFor) },
      {
        onSuccess: (saved) => toaster.create({
          title: saved.own === null ? 'Arrival instruction removed' : 'Arrival instruction saved',
          type: 'success',
          duration: 3000,
        }),
      },
    )
  }

  return (
    <Card p={{ base: 4, md: 6 }}>
      <VStack align="stretch" gap={4}>
        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'flex-start' }} gap={2}>
          <VStack align="stretch" gap={1}>
            <Text fontSize="lg" fontWeight="semibold">Arrival instructions (shown to canvassers)</Text>
            <Text fontSize="sm" color="fg.muted">{sourceLine(view, chainLabel)}</Text>
          </VStack>
          <Button
            variant="outline"
            colorPalette="brand"
            minH="44px"
            disabled={!own || dirty}
            onClick={() => setCopyOpen(true)}
          >
            <FiCopy />Copy to…
          </Button>
        </Stack>

        {!!own && dirty && (
          <Text fontSize="sm" color="fg.muted">Save first to copy your changes</Text>
        )}
        {kind === 'worksite' && view?.effective?.source === 'Business' && (
          <Text fontSize="sm" color="info.fg">Saving here overrides your chain instruction for this worksite</Text>
        )}
        {kind === 'business' && (
          <Text fontSize="sm" color="info.fg">A worksite's own instruction overrides this one</Text>
        )}

        <Field.Root>
          <Field.Label>Text</Field.Label>
          <Textarea value={text} rows={3} onChange={e => setText(e.target.value)} />
          <CapCounter length={text.length} cap={textCap} />
        </Field.Root>
        <Field.Root>
          <Field.Label>Text (Spanish)</Field.Label>
          <Textarea value={textEs} rows={3} onChange={e => setTextEs(e.target.value)} />
          <CapCounter length={textEs.length} cap={textCap} />
        </Field.Root>
        <Field.Root>
          <Field.Label>Ask for</Field.Label>
          <Input value={askFor} minH="44px" onChange={e => setAskFor(e.target.value)} />
          <CapCounter length={askFor.length} cap={askForCap} />
        </Field.Root>

        {!!save.error && (
          <Text fontSize="sm" color="danger.fg">
            {extractApiMessage(save.error, 'Could not save the arrival instruction')}
          </Text>
        )}

        <HStack justify="flex-end">
          <Button colorPalette="brand" minH="44px" loading={save.isPending} onClick={handleSave}>Save</Button>
        </HStack>
      </VStack>

      {copyOpen && (
        <CopyArrivalModal kind={kind} sourceId={id} open={copyOpen} onClose={() => setCopyOpen(false)} />
      )}
    </Card>
  )
}
